import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useFetchPriceSuggestionTypesWithCount } from '@retail/price-suggestion/data-access';
import { getAvailableTypes } from '@retail/price-suggestion/utils';
import { ConditionsNew } from '@retail/calculus/types';
import { AllConditionsFormValues } from '../allConditionsSchema';
import { CreateSalesPriceConditionDialogContent } from '../../../SalesPriceConditions/CreateSalesPriceCondition';
import { InheritedConditionHint } from './InheritedConditionHint';

interface Props {
  supplierNumber?: number;
  editCondition?: ConditionsNew;
}

export function SalesPriceStep({ supplierNumber, editCondition }: Props) {
  const { assortmentId, appTenant, orgUnitType } = useSelectedOrgUnit();
  const { watch } = useFormContext<AllConditionsFormValues>();

  const { userCanCreate, userCanRead } = usePermissionsForCurrentUser();
  const includeMarkup = userCanCreate('PRICE_CALCULATION__MARKUP');
  const includeLocalPriceSuggestion = userCanRead('PRICE_SUGGESTIONS');

  const { data: priceSuggestionTypesWithCount = [], isLoading: isLoadingFetchPriceSuggestions } =
    useFetchPriceSuggestionTypesWithCount(
      {
        assortmentId,
        mgSupplierId: supplierNumber,
        productGroup: (watch('productGroup') ?? watch('mainGroup') ?? watch('overGroup'))
          ?.groupNumber,
        mgItemNumber: watch('item')?.mgItemNumber,
      },
      { suspense: false }
    );

  const filteredPriceSuggestions = useMemo(
    () =>
      priceSuggestionTypesWithCount.filter((type) =>
        getAvailableTypes({
          countryCode: appTenant.countryCode,
          orgUnitType,
          canReadLocal: includeLocalPriceSuggestion,
          assortmentLevel: !supplierNumber,
        }).includes(type.suggestionType)
      ),
    [
      appTenant.countryCode,
      orgUnitType,
      priceSuggestionTypesWithCount,
      supplierNumber,
      includeLocalPriceSuggestion,
    ]
  );

  return (
    <>
      {editCondition && <InheritedConditionHint area="salesPrice" condition={editCondition} />}
      <CreateSalesPriceConditionDialogContent
        priceSuggestionTypes={filteredPriceSuggestions}
        includeMarkup={includeMarkup}
        supplierNumber={supplierNumber}
        isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
      />
    </>
  );
}
